import { noop } from 'lodash';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import pluralize from 'pluralize';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type CanvasSyncMutations, useEditCanvasStore } from '@air/feature-edit-canvas';
import { ModalProvider, useAirModal } from '@air/provider-modal';
import { ShortIdProvider } from '@air/provider-short-id';
import { ErrorBoundary } from '@air/utils-error';

import { usePublicPageViewNotification } from '~/components/AirNotifications/hooks/usePublicPageViewNotification';
import { convertCanvasNodesToAppNodes } from '~/components/Canvas/CanvasRightPanelContent/utils/convertCanvasNodesToAppNodes';
import { PublicCanvasHeader } from '~/components/Layouts/PublicCanvasLayout/PublicCanvasHeader';
import { PublicCanvasTopBar } from '~/components/Layouts/PublicCanvasLayout/PublicCanvasTopBar';
import { PublicCanvasAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicCanvasAnonymousSignUpModal';
import { PublicWorkspaceProvider } from '~/components/PublicWorkspaceProvider';
import { PublicPageSeoTitle } from '~/components/v2/components/shared/seo/PublicPageSeoTitle';
import { QueryParamNames } from '~/constants/search';
import { useOnMissingUserSession } from '~/hooks/useOnMissingUserSession';
import { PublicAccountProvider, useAccountContext } from '~/providers/AccountProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { ReCaptchaProvider } from '~/providers/ReCaptchaProvider';
import { type CanvasDetail } from '~/swr-hooks/canvases/types';
import { getShareSeoDescription } from '~/swr-hooks/shortUrl/utils';

const EditCanvas = dynamic(
  () => import('@air/feature-edit-canvas/canvas').then((mod) => ({ default: mod.EditCanvas })),
  {
    ssr: false,
  },
);

export type CanvasCreator = {
  id: string;
  firstName: string;
  lastName: string;
  avatar?: string | null;
  initials: string;
};

export type PublicCanvasProps = {
  shortId: string;
  initialData: {
    canvas: {
      id: string;
      title: string;
      nodes: CanvasDetail['nodes'];
    };
    workspace: {
      id: string;
      name: string;
      image?: string | null;
    };
    creator?: CanvasCreator;
    requireAccount: boolean;
    isOnPaidPlan: boolean | undefined;
  };
};

/**
 * The canvas-typed member of `getByShortId`'s response, stated structurally —
 * `GetByShortIDCanvasResponse` is not re-exported from `@air/api` (unlike the other members).
 */
type PublicCanvasShareResponse = {
  requireAccount: boolean;
  isOnPaidPlan?: boolean;
  data: {
    id: string;
    title: string;
    nodes?: CanvasDetail['nodes'];
    workspace?: { id: string; name: string; image?: string | null };
    creator?: CanvasCreator;
  };
};

/**
 * Builds a canvas share link's `initialData` from a resolved `getByShortId` response. Shared by the
 * route's getStaticProps (ISR) and its passcode gate (post-verification) — a passcode-protected
 * link's data can only ever be fetched client-side, so both call sites package the same response
 * shape. Pure mapping (no fetch), so it lives with the type it constructs rather than with the
 * shortUrl fetchers.
 */
export const buildPublicCanvasInitialData = (
  response: PublicCanvasShareResponse,
): PublicCanvasProps['initialData'] => ({
  canvas: {
    id: response.data.id,
    title: response.data.title,
    nodes: response.data.nodes ?? [],
  },
  workspace: {
    id: response.data.workspace?.id ?? '',
    name: response.data.workspace?.name ?? '',
    image: response.data.workspace?.image ?? null,
  },
  creator: response.data.creator,
  requireAccount: response.requireAccount,
  isOnPaidPlan: response.isOnPaidPlan,
});

const NOOP = async (): Promise<{ nodes: { id: string }[] }> => ({ nodes: [] });
const NOOP_RENAME = async () => noop();

type PublicCanvasInnerProps = Pick<PublicCanvasProps, 'shortId' | 'initialData'>;

const PublicCanvasInner = memo(({ shortId, initialData }: PublicCanvasInnerProps) => {
  const { requireAccount } = initialData;
  const { query } = useRouter();
  const previewNodeId = typeof query[QueryParamNames.preview] === 'string' ? query[QueryParamNames.preview] : undefined;
  const { data: account } = useAccountContext();
  const [showAnonymousSignupModal] = useAirModal(PublicCanvasAnonymousSignUpModal);
  const nodeCount = useEditCanvasStore((state) => state.nodes.length);
  const nodes = useMemo(() => convertCanvasNodesToAppNodes(initialData.canvas.nodes), [initialData.canvas.nodes]);

  const handleMissingSession = useCallback(() => {
    showAnonymousSignupModal({});
  }, [showAnonymousSignupModal]);

  useOnMissingUserSession({ onMissingSession: handleMissingSession, enabled: requireAccount });

  const { sendPublicPageViewEvent } = usePublicPageViewNotification();

  useEffect(() => {
    if (!requireAccount || (requireAccount && !!account)) {
      sendPublicPageViewEvent(shortId);
    }
  }, [account, requireAccount, sendPublicPageViewEvent, shortId]);

  useIsomorphicLayoutEffect(() => {
    useEditCanvasStore.setState({ nodes });
  }, [nodes]);

  const syncMutations = useMemo<CanvasSyncMutations>(
    () => ({
      canvasId: initialData.canvas.id,
      createNodes: NOOP,
      deleteNodes: NOOP,
      updateNodes: NOOP,
      renameCanvas: NOOP_RENAME,
      workspaceId: initialData.workspace.id,
    }),
    [initialData.canvas.id, initialData.workspace.id],
  );

  const copyShareUrl = useCallback((previewNodeId?: string) => {
    const url = new URL(window.location.href);

    if (previewNodeId) {
      url.searchParams.set(QueryParamNames.preview, previewNodeId);
    }
    void navigator.clipboard.writeText(url.toString());
    return true;
  }, []);

  return (
    <div data-testid="PUBLIC_CANVAS" className="flex h-screen w-screen flex-col bg-gray-4">
      <PublicPageSeoTitle title={initialData.canvas.title} description={getShareSeoDescription()} />
      <PublicCanvasHeader />
      <div className="flex-1 overflow-hidden pb-2 pl-2">
        <EditCanvas
          canDownload={false}
          initialPreviewNodeId={previewNodeId}
          onShareClick={copyShareUrl}
          onSharePreview={copyShareUrl}
          readOnly
          syncMutations={syncMutations}
          title={initialData.canvas.title}
          topBar={<PublicCanvasTopBar title={initialData.canvas.title} />}
        />
      </div>
      <div className="flex shrink-0 items-center px-4 py-2">
        <span className="font-medium text-12 text-gray-11">{pluralize('item', nodeCount, true)}</span>
      </div>
    </div>
  );
});

PublicCanvasInner.displayName = 'PublicCanvasInner';

export const PublicCanvas = memo(({ shortId, initialData }: PublicCanvasProps) => {
  const workspace = useMemo(
    () => ({ id: initialData.workspace.id, name: initialData.workspace.name }),
    [initialData.workspace.id, initialData.workspace.name],
  );

  return (
    <ReCaptchaProvider siteKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}>
      <PublicAccountProvider workspace={workspace}>
        <ExternalLinksClickableProvider areExternalLinksClickable={initialData.isOnPaidPlan === true}>
          <ShortIdProvider
            shortId={shortId}
            objectId={initialData.canvas.id}
            objectType="canvas"
            isOnPaidPlan={initialData.isOnPaidPlan}
          >
            <ModalProvider>
              <PublicWorkspaceProvider
                workspaceId={initialData.workspace.id}
                workspaceImage={initialData.workspace.image ?? ''}
                workspaceName={initialData.workspace.name}
              >
                <ErrorBoundary>
                  <PublicCanvasInner shortId={shortId} initialData={initialData} />
                </ErrorBoundary>
              </PublicWorkspaceProvider>
            </ModalProvider>
          </ShortIdProvider>
        </ExternalLinksClickableProvider>
      </PublicAccountProvider>
    </ReCaptchaProvider>
  );
});

PublicCanvas.displayName = 'PublicCanvas';
